import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShieldCheckIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { paymentApi } from '../../api/paymentApi';

const PaymentCheckout = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);

  const handleSimulatePayment = async (status) => {
    setProcessing(true);
    try {
      if (status === 'success') {
        // Find the payment id by fetching payments and matching order ID
        // (In a real app, Razorpay returns payment details. We will simulate verification)
        const res = await paymentApi.getPayments();
        if (res.data.success) {
          const payment = res.data.data.find(p => p.razorpayOrderId === orderId);
          if (!payment) {
            toast.error("Payment Order not found!");
            setProcessing(false);
            return;
          }

          // Simulate verification
          const verifyRes = await paymentApi.verifyPayment({
            razorpay_order_id: orderId,
            razorpay_payment_id: `mock_pay_${Math.floor(Math.random()*10000)}`,
            razorpay_signature: "mock_signature",
            paymentId: payment._id,
            isMock: true
          });

          if (verifyRes.data.success) {
            toast.success("Payment Successful! Ledger has been updated.");
            setTimeout(() => navigate('/'), 2000);
          } else {
            toast.error("Payment verification failed.");
          }
        }
      } else {
        toast.error("Payment cancelled or failed.");
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "An error occurred");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-xl max-w-sm w-full border border-gray-100 dark:border-gray-700 text-center relative overflow-hidden">
        
        {/* Decorative background */}
        <div className="absolute -top-12 -right-12 w-32 h-32 bg-blue-50 dark:bg-blue-900/20 rounded-full blur-2xl"></div>
        <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-green-50 dark:bg-green-900/20 rounded-full blur-2xl"></div>

        <div className="relative z-10">
          <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
            <ShieldCheckIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>

          <h2 className="text-xl font-extrabold text-gray-900 dark:text-white mb-2">Simulate Payment</h2>
          <p className="text-[13px] text-gray-500 dark:text-gray-400 mb-6 font-medium">
            Order ID: <span className="font-bold text-gray-700 dark:text-gray-300">{orderId}</span>
          </p>

          <div className="space-y-4">
            <button 
              onClick={() => handleSimulatePayment('success')}
              disabled={processing}
              className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-white py-3.5 rounded-xl font-bold hover:bg-[#1DA851] transition-colors shadow-sm disabled:opacity-70"
            >
              <CreditCardIcon className="w-5 h-5" />
              {processing ? 'Processing...' : 'Simulate Successful Payment'}
            </button>
            <button 
              onClick={() => handleSimulatePayment('fail')}
              disabled={processing}
              className="w-full flex items-center justify-center gap-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-3.5 rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-70"
            >
              Cancel Payment
            </button>
          </div>

          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-6">
            In a real environment, this page would load the Razorpay checkout modal via their SDK. We are using a mock screen since real API keys aren't configured.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentCheckout;
